import { PlanningService } from "@3gaam/api/services";
import { ExamDatesQueryOutput, ExamDatePortable } from "@3gaam/api/types/planning";
import { ExamDatesQueryInput } from "@3gaam/api/types/planningMapped";
import {
  ComplexPackageConfigurationSelection,
  ComplexPackageCounselorFieldOption,
  ComplexPackageCounselorOutput,
} from "@3gaam/api/types/store";
import { DatePickerField } from "@3gaam/shared/components/general/form";
import SelectField from "@3gaam/shared/components/general/form/selectField";
import Icon from "@3gaam/shared/components/general/icon";
import { Space } from "@3gaam/shared/components/general/space";
import { usePagantionQuery } from "@3gaam/shared/hooks/useQuery";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker2";

import { useModal } from "../modals/useModalStore";
import { usePackageSettings } from "../usePackageSettings";

type Props = {
  complexPackage: ComplexPackageCounselorOutput;
};

const Settings = ({ complexPackage }: Props) => {
  let {
    setSelectedDate,
    setSelectedGoal,
    selectedGoal,
    resetPackageSettings,
  } = usePackageSettings();
  const [examDatesQueryList, setExamDatesQueryList] =
    useState<ExamDatesQueryOutput["Items"]>(undefined);

  let { openModal } = useModal();

  let [{ data: examDatesData, isRequesting: isRequestingExamDatesData }, getExamDates,] = usePagantionQuery<ExamDatesQueryInput, ExamDatePortable, ExamDatesQueryOutput>(
    "pcappExamDates",
    PlanningService.examDatesQuery,
  );

  useEffect(() => {
    if (!selectedGoal) return;
    if (!selectedGoal.ExamId) return;
    getExamDates({ "Filter.ExamId": selectedGoal.ExamId,  PageSize: 500 });
  }, [getExamDates, selectedGoal]);

  useEffect(() => {
    if (isRequestingExamDatesData) return;
    if (!examDatesData || !examDatesData) return;
    setExamDatesQueryList(examDatesData);
    if (examDatesData.length === 1 && complexPackage.ComplexPackageId) {
      setSelectedDate({
        packageId: complexPackage.ComplexPackageId,
        selectedExamDateId: examDatesData?.[0].ExamDateId,
      });
    }
  }, [
    complexPackage.ComplexPackageId,
    examDatesData,
    isRequestingExamDatesData,
    setSelectedDate,
  ]);

  useEffect(() => {
    if (complexPackage.Goals?.length != 1 || !complexPackage.ComplexPackageId) {
      resetPackageSettings();
      return;
    }
    setSelectedGoal(complexPackage.ComplexPackageId, complexPackage.Goals[0]);
  }, [
    complexPackage.ComplexPackageId,
    complexPackage.Goals,
    resetPackageSettings,
    setSelectedGoal,
  ]);

  let pickerRef = useRef<DatePicker>();

  let showGoalPicker =
    complexPackage.Goals &&
    complexPackage.Goals.length >= 1 &&
    complexPackage.GoalSelection ===
      ComplexPackageConfigurationSelection.Selected;

  if (!showGoalPicker) return null;
  return (
    <>
      <Space vertical={32} />

      <div className="px-3" style={{ position: "relative", zIndex: 5 }}>
        <h3 className="font-16 text-dark-gray">تنظیمات بسته</h3>
        <Space vertical={4} />
        <span className="font-12 text-light-gray2">
          برای مشخص شدن هزینه، گرایش و زمان آزمون خود را انتخاب کنید
        </span>
        {showGoalPicker && (
          <div style={{ position: "relative", zIndex: 1 }}>
            <Space vertical={16} />
            <span className="text-gray3 font-14">موضوع</span>
            <Space vertical={8} />
            <div
              className={`d-flex justify-content-between align-items-center mt-2 rounded radius-8 border p-3 bg-white h-48 ${
                !!selectedGoal ? "text-dark-gray" : "text-gray4"
              }`}
              onClick={() => {
                if (!complexPackage.Goals) return;
                if (complexPackage.Goals.length <= 1) return;
                openModal({
                  name: "customPopup",
                  payload: {
                    rawItems: complexPackage.Goals,
                    title: "موضوع",
                    onItemClick: (item) => {
                      if (!complexPackage.ComplexPackageId) return;
                      setSelectedGoal(
                        complexPackage.ComplexPackageId,
                        item as ComplexPackageCounselorFieldOption,
                      );
                      setSelectedDate({
                        packageId: complexPackage.ComplexPackageId,
                        selectedCustomExamDate: undefined,
                        selectedExamDateId: undefined,
                      });
                      setExamDatesQueryList(undefined);
                      pickerRef.current?.setState((state) => ({
                        ...state,
                        inputValue: undefined,
                        momentValue: null,
                      }));
                    },
                  },
                });
              }}
            >
              {selectedGoal?.Label || "انتخاب کنید"}
              {complexPackage.Goals && complexPackage.Goals.length > 1 ? (
                <span
                  onClick={() => {}}
                  className="d-flex align-items-center font-14 text-gray3"
                >
                  <Icon
                    name="far-fa-angle-left"
                    className="font-24"
                    style={{ lineHeight: 0 }}
                  />
                </span>
              ) : null}
            </div>
          </div>
        )}
        <Space vertical={8} />
        {selectedGoal?.ExamId ? (
          <div
            className=""
            style={{
              position: "relative",
              zIndex: 4,
              ...(selectedGoal ? {} : { pointerEvents: "none", opacity: 0.5 }),
            }}
          >
            <Space vertical={16} />
            <span className="text-gray3 font-14">زمان آزمون</span>
            <Space vertical={8} />
            {selectedGoal &&
            examDatesQueryList &&
            examDatesQueryList?.length > 0 ? (
              <>
                <SelectField
                  options={examDatesQueryList.map((item) => {
                    return { label: item.Title, value: item.ExamDateId };
                  })}
                  height={48}
                  name="زمان آزمون"
                  onChange={(selected) => {
                    if (!complexPackage.ComplexPackageId) return;
                    if (typeof selected.target.value !== "undefined") {
                      setSelectedDate({
                        packageId: complexPackage.ComplexPackageId,
                        selectedExamDateId: selected.target.value,
                      });
                    } else {
                      setSelectedDate({
                        packageId: complexPackage.ComplexPackageId,
                        selectedCustomExamDate: undefined,
                        selectedExamDateId: undefined,
                      });
                    }
                  }}
                  placeholder="زمان آزمون خود را مشخص کنید"
                  isClearable={true}
                />
              </>
            ) : (
              <DatePickerField
                className="h-48"
                pickerRef={pickerRef}
                onChange={(e: any) => {
                  if (!complexPackage.ComplexPackageId) return;
                  //@ts-ignore
                  if (!pickerRef.current?.state?.inputValue) return;
                  setSelectedDate({
                    packageId: complexPackage.ComplexPackageId,
                    selectedCustomExamDate: String(
                      e.target.value.format("YYYY-MM-DD"),
                    ),
                  });
                }}
                name="examDate"
                placeholder="زمان آزمون خود را مشخص کنید"
                icon="far-fa-calendar-day"
                // error={error}
              />
            )}
          </div>
        ) : null}
        <Space vertical={8} />
      </div>
    </>
  );
};

export default Settings;
